<template>
  <div 
    class="notification"
    :class="[type, { 'is-dismissible': dismissible }]"
    v-if="visible"
  >
    <div class="notification-icon">
      <span class="material-icons">{{ getIcon() }}</span>
    </div>
    <div class="notification-content">
      <div class="notification-title">{{ title }}</div>
      <div class="notification-message">{{ message }}</div>
    </div>
    <button 
      v-if="dismissible" 
      class="notification-dismiss" 
      @click="dismiss"
      aria-label="Fermer la notification"
    >
      <span class="material-icons">close</span>
    </button>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits } from 'vue';

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  message: {
    type: String,
    required: true
  },
  type: {
    type: String,
    default: 'info',
    validator: (value) => ['info', 'success', 'warning', 'error'].includes(value)
  },
  dismissible: {
    type: Boolean,
    default: true
  },
  autoClose: {
    type: Boolean,
    default: false
  },
  duration: {
    type: Number,
    default: 5000
  }
});

const emit = defineEmits(['close']);
const visible = ref(true);

// Fermer automatiquement la notification après la durée spécifiée
if (props.autoClose && props.duration > 0) {
  setTimeout(() => {
    dismiss();
  }, props.duration);
}

function dismiss() {
  visible.value = false;
  emit('close');
}

function getIcon() {
  switch (props.type) {
    case 'success':
      return 'check_circle';
    case 'warning':
      return 'warning';
    case 'error':
      return 'error';
    case 'info':
    default:
      return 'info';
  }
}
</script>

<style scoped>
/* Import des variables de couleurs OpSolver */
@import '@/assets/css/variables.css';

.notification {
  display: flex;
  align-items: flex-start;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.notification-icon {
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification-icon .material-icons {
  font-size: 24px;
}

.notification-content {
  flex: 1;
}

.notification-title {
  font-weight: 600;
  margin-bottom: 4px;
}

.notification-message {
  font-size: 14px;
  line-height: 1.4;
}

.notification-dismiss {
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 12px;
  padding: 4px;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.notification-dismiss:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.notification-dismiss .material-icons {
  font-size: 18px;
}

/* Types de notification */
.notification.info {
  background-color: var(--turquoise-pale, #E0F7F4);
  border-left: 4px solid var(--turquoise-primary, #30C7B5);
}

.notification.info .notification-icon {
  color: var(--turquoise-primary, #30C7B5);
}

.notification.success {
  background-color: #e6f7ed;
  border-left: 4px solid var(--turquoise-dark, #26A194);
}

.notification.success .notification-icon {
  color: var(--turquoise-dark, #26A194);
}

.notification.warning {
  background-color: #fff8e6;
  border-left: 4px solid #f0ad4e;
}

.notification.warning .notification-icon {
  color: #f0ad4e;
}

.notification.error {
  background-color: #fbeae9;
  border-left: 4px solid var(--coral-soft, #FF7A68);
}

.notification.error .notification-icon {
  color: var(--coral-soft, #FF7A68);
}
</style>
